"use client";

import { useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import useEmblaCarousel from "embla-carousel-react";
import {
  ArrowUpRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

import type { TourDto } from "@tripcart/shared";

import { useFormatMoney } from "@/lib/currency-format-context";
import { usePermalinkPath } from "@/lib/permalinks-context";
import { useTranslator } from "@/lib/translation-context";
import { hasDisplayPrice } from "@/lib/price-display";
import {
  HERO_FALLBACK_IMAGE,
  HERO_FALLBACK_ORIGIN,
} from "@/lib/hero-fallback-image";

import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./_tour-card.client.module.css";

interface TourCardProps {
  tour: TourDto;
  /** Eagerly load this card's first image as an LCP candidate. Set on the
   *  first card of the experience-type grid, where there's no hero image
   *  above the grid so the first card IS the largest contentful paint. */
  priority?: boolean;
}

/**
 * Destination-page tour card. Pixel intent ported from the dn.html mockup:
 *   - 3:3.5 aspect image carousel (Embla) with hover-revealed nav arrows
 *   - Bottom-anchored body overlay with a dark gradient mask so the
 *     headline and price read against any photo
 *   - Card lifts on hover (translateY + deeper shadow)
 *
 * Each card owns its own Embla instance so they swipe independently. Embla
 * is already installed for the BlockNote image-carousel block, so no new
 * dependency.
 */
export function TourCard({
  tour,
  priority = false,
}: TourCardProps): React.ReactElement {
  const formatPrice = useFormatMoney();
  const t = useTranslator();
  const path = usePermalinkPath();
  const hasPrice = hasDisplayPrice(tour.priceFrom);
  // The caption under the price states a fact about THIS tour, never a generic
  // one. "Price varies by group size" is only true when the operator actually
  // configured tiered group pricing — `hasGroupPricing`, the same signal the
  // tours-listing card gates its "Group Discounts" pill on. It used to render
  // for every priced tour, telling guests the price moved with head-count on
  // tours whose price is flat. Otherwise fall back to the operator's own unit
  // label ("per person"), and to nothing when there isn't one — an absent
  // caption beats an invented one.
  const priceCaption = tour.hasGroupPricing
    ? t("Price varies by group size")
    : (tour.soldAs ?? null);
  // A tour with no cover photo still arrives with `heroImage: { url: "" }`
  // (the API's resolveHero fills an empty url when nothing is uploaded), so
  // keep only entries that actually resolve — otherwise the carousel paints a
  // broken `<Image src="">`. An empty result falls through to the branded
  // placeholder below.
  const images = (
    tour.gallery && tour.gallery.length > 0
      ? tour.gallery
      : tour.heroImage
        ? [tour.heroImage]
        : []
  ).filter((img) => Boolean(img.url?.trim()));

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: images.length > 1 });

  const scrollPrev = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollPrev();
    },
    [emblaApi],
  );
  const scrollNext = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollNext();
    },
    [emblaApi],
  );

  return (
    <Link
      href={path("tour", tour.slug)}
      aria-label={t("View Tour: {name}", { name: tour.name })}
      // Hover transition matches dn.html byte-for-byte:
      //   - transform: 250ms with cubic-bezier(.16, 1, .3, 1) — sharp
      //     ease-out so the lift feels confident, not draggy.
      //   - box-shadow: 250ms with plain ease — separate timing so the
      //     two animations don't fight each other (shadow ramps in
      //     more gently than the transform, which is what gives the
      //     "premium" feel; same easing on both reads as plastic).
      // `will-change: transform` pre-promotes the card to a compositor
      // layer so the first hover doesn't trigger a layout repaint.
      style={{
        transition: "transform 250ms var(--ease-out-5), box-shadow 250ms ease",
        willChange: "transform",
      }}
      className={styles.card}
    >
      {/* Image carousel — 3:3.5 portrait aspect */}
      <div className={styles.media} style={{ aspectRatio: "3 / 3.5" }}>
        {images.length > 0 ? (
          <div className={styles.viewport} ref={emblaRef}>
            <div className={styles.track}>
              {images.map((img, imgIdx) => (
                <div key={img.id ?? img.url} className={styles.slide}>
                  <Image
                    src={img.url}
                    alt={img.alt || tour.name}
                    fill
                    sizes="(min-width: 1060px) 33vw, (min-width: 600px) 50vw, 100vw"
                    priority={priority && imgIdx === 0}
                    className={styles.image}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          // No cover photo uploaded — the same stock landscape the
          // destination/experience-type hero falls back to (never a flat
          // brand-tinted panel) so an untouched tour still reads as a
          // photographed place, not a placeholder. The branded gradient stays
          // UNDER it as the paint-time base, same layering as the hero. The
          // bottom overlay + title/price render on top either way.
          <>
            <link rel="preconnect" href={HERO_FALLBACK_ORIGIN} />
            <div aria-hidden className={styles.fallback} />
            <Image
              src={HERO_FALLBACK_IMAGE.destination}
              alt=""
              fill
              sizes="(min-width: 1060px) 33vw, (min-width: 600px) 50vw, 100vw"
              priority={priority}
              quality={55}
              className={styles.image}
            />
          </>
        )}

        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label={t("Previous Photo")}
              onClick={scrollPrev}
              className={cx(styles.navButton, styles.prev)}
            >
              <ChevronLeftIcon strokeWidth={2} />
            </button>
            <button
              type="button"
              aria-label={t("Next Photo")}
              onClick={scrollNext}
              className={cx(styles.navButton, styles.next)}
            >
              <ChevronRightIcon strokeWidth={2} />
            </button>
          </>
        )}

        {/* Body overlay — gradient base + title + price, bottom-anchored */}
        <div className={styles.overlay}>
          <h3 className={styles.title}>{tour.name}</h3>
          <div className={styles.priceRow}>
            <div className={styles.priceColumn}>
              {hasPrice ? (
                <>
                  <div className={styles.priceLine}>
                    <span className={styles.fromLabel}>
                      {t("Starting From")}
                    </span>
                    <span className={styles.price}>
                      {formatPrice(tour.priceFrom?.amount ?? 0)}
                    </span>
                  </div>
                  {priceCaption ? (
                    <div className={styles.caption}>{priceCaption}</div>
                  ) : null}
                </>
              ) : (
                <span className={styles.comingSoon}>
                  {t("Pricing Coming Soon")}
                </span>
              )}
            </div>
            {/* Arrow affordance — white 40px circle in the bottom-right,
                matching the dn.html mockup. Sits inside the parent
                <Link>, so clicking the arrow navigates to the detail
                page exactly like clicking the title.

                Animation: 500ms with Material standard easing
                (cubic-bezier(0.4, 0, 0.2, 1)) — long enough to be
                visibly smooth, balanced curve distributes motion
                evenly so the transition never feels snappy or
                instant. Circle and arrow nudge in concert; the
                slightly longer arrow duration creates a subtle
                follow-through that reads as polish. */}
            <span
              aria-hidden
              style={{
                transition:
                  "transform 500ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 500ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              className={styles.arrow}
            >
              <ArrowUpRightIcon
                style={{
                  transition: "transform 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                className={styles.arrowIcon}
                strokeWidth={2}
              />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
